import React, { useCallback, ReactChild, useState, useRef, useLayoutEffect, useEffect } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import Infinite from 'react-infinite';
import styled from 'styled-components';
import { detectOS } from 'detect-browser';
import _ from 'lodash';

import { measureReactElements, useElementDimensions } from '@/lib/layout';
import { store } from '@/redux/app/store';
import { useCachedValue, usePersistedValue, usePreviousValue } from '@/lib/react-hooks';

const PAGINATION_OFFSET = 500; // in px

class PatchedInfinite extends Infinite {
  constructor(props) {
    super(props);

    if (detectOS(window.navigator.userAgent) !== 'iOS') return;

    // There's an issue with the way scrolling events work on iOS that results in the
    // scrolltop value getting reset during pagination, so we need to patch the
    // implementation and prevent it as much as possible
    const inst = this as any;
    const { recomputeInternalStateFromProps } = inst;
    inst.recomputeInternalStateFromProps = (nextProps) => {
      const state = recomputeInternalStateFromProps(nextProps);

      if (!nextProps.useWindowAsScrollContainer) {
        state.utils.getScrollTop = () => {
          if (!inst.scrollable) return 0;
          let scrolltop = inst.scrollable.scrollTop;
          if (inst.inertScrollTop !== undefined
            && Math.abs(inst.inertScrollTop - scrolltop) > 1000
          ) {
            scrolltop = inst.inertScrollTop;
            inst.scrollable.scrollTop = scrolltop;
          } else {
            inst.inertScrollTop = scrolltop;
          }
          return scrolltop;
        };
        state.utils.setScrollTop = (value) => {
          if (inst.scrollable) {
            inst.scrollable.scrollTop = value;
            inst.inertScrollTop = value;
          }
        };
      }

      return state;
    };
  }
}

interface Props {
  isNextPageLoading: boolean;
  loadNextPage: () => void;
  height?: number;
  loader?: Infinite.InfiniteProps['loadingSpinnerDelegate'];
}

const ScrollLatestContainer = styled.div`
  position: absolute;
  bottom: 0px;
  width: 100%;
  text-align: center;
`;

const ScrollLatestInner = styled.div`
  background: rgba(0,0,0,0.5);
  color: #fff;
  padding: 5px 10px;
  border-radius: 500px;
  margin: 10px 0;
  display: inline-block;
  cursor: pointer;
`;
export interface BasicProps extends Props {
  children: JSX.Element[];
  containerRef: React.MutableRefObject<HTMLDivElement>;
  rowHeight: number|number[];
  scrollLatest?: boolean;
}
export interface DynamicProps extends Props {
  list: string[];
  // Needed to correctly update list items that can re-render and change their heights (e.g. Redux)
  renderIds?: string[];
  renderItem: (item: this['list'][number], index: number) => ReactChild;
  assumeSameRowHeight?: boolean;
  // If the scroll distance to the bottom is less than this value in px,
  // appending new items to the bottom resets scroll position
  stickyAppendMargin?: number;
  scrollLatest?: boolean;
}

export const Basic = React.forwardRef(({
  children, loader, isNextPageLoading, loadNextPage, height, rowHeight, containerRef
}: BasicProps, ref: React.RefObject<Infinite>) => {
  const [scrollLatest, setScrollLatest] = useState(false);
  const loadMoreRows = useCallback(() => {
    if (!isNextPageLoading) loadNextPage();
  }, [isNextPageLoading, loadNextPage]);
  const inst: any = ref.current;
  let scrollTop;
  if (inst) {
    scrollTop = inst.utils.getScrollTop();
  }

  const handleScrollBottom = () => {
    const scrollDiff = inst.getLowestPossibleScrollTop();
    inst.handleScroll(scrollDiff, 'handleScrollBottom');
    setScrollLatest(false);
  };
  // check if we are scrolled futher up
  useEffect(() => {
    if (!inst) return;
    const scrollDiff = inst.getLowestPossibleScrollTop();
    const scrollPosition = scrollDiff - scrollTop;
    if (scrollPosition > 600) {
      setScrollLatest(true);
    } else {
      setScrollLatest(false);
    }
  }, [scrollTop]);

  let content: JSX.Element;
  if (children) {
    content = typeof height === 'number'
      ? (
        <Infinite
          ref={ref}
          displayBottomUpwards
          containerHeight={height}
          elementHeight={rowHeight}
          infiniteLoadBeginEdgeOffset={PAGINATION_OFFSET}
          onInfiniteLoad={loadMoreRows}
          loadingSpinnerDelegate={loader}
          isInfiniteLoading={isNextPageLoading}
        >
          {children}
        </Infinite>
      )
      : (
        <AutoSizer disableWidth>
          {({ height: calculatedHeight }) => (
            !calculatedHeight ? null : (
              <PatchedInfinite
                ref={ref}
                displayBottomUpwards
                containerHeight={calculatedHeight || -1}
                elementHeight={rowHeight}
                infiniteLoadBeginEdgeOffset={PAGINATION_OFFSET}
                onInfiniteLoad={loadMoreRows}
                loadingSpinnerDelegate={loader}
                isInfiniteLoading={isNextPageLoading}
              >
                {children}
              </PatchedInfinite>
            )
          )}
        </AutoSizer>
      );
  }
  return (
    <div id="basic-inner-container" ref={containerRef} style={{ height: '100%' }}>
      {content || null}
      {scrollLatest && (
        <ScrollLatestContainer onClick={handleScrollBottom}>
          <ScrollLatestInner>
            Scroll to Bottom
          </ScrollLatestInner>
        </ScrollLatestContainer>
      )}
    </div>
  );
});

export const Dynamic = ({
  list, renderIds, renderItem, assumeSameRowHeight = true, loadNextPage, isNextPageLoading,
  stickyAppendMargin, scrollLatest,
  ...rest
}: DynamicProps) => {
  const [eventsContainerWidth, setEventsContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>();
  const [computedRowHeight, setComputedRowHeight] = useState<number|number[]>();
  const savedMeasurementsWrapper = usePersistedValue({});
  const savedMeasurements = savedMeasurementsWrapper.get();
  const isMeasuring = usePersistedValue(false);
  useLayoutEffect(() => {
    if (list.length < 1) setComputedRowHeight(undefined);
    else {
      const newMeasurements = {};
      const elementsToMeasure = [];
      const renderIdsToMeasure = [];
      (assumeSameRowHeight ? list.slice(0, 1) : list).forEach((item, ix) => {
        const element = (
          <div
            className="ticket-view-container"
            style={{ padding: '0px 15px' }}
          >
            {renderItem(item, ix)}
          </div>
        );
        const renderId = renderIds ? renderIds[ix] : item;
        const savedValue = savedMeasurements[renderId];

        if (!savedValue) {
          elementsToMeasure.push(element);
          renderIdsToMeasure.push(renderId);
        } else {
          newMeasurements[renderId] = savedValue;
        }
      });

      if (elementsToMeasure.length > 0) {
        isMeasuring.set(true);

        measureReactElements(containerRef.current, elementsToMeasure, store).then((heights) => {
          heights.forEach((height, ix) => {
            newMeasurements[renderIdsToMeasure[ix]] = height;
          });
          savedMeasurementsWrapper.set(newMeasurements);

          const rowHeights = (renderIds || list).map((renderId) => newMeasurements[renderId]);
          isMeasuring.set(false);
          setComputedRowHeight(rowHeights);
        });
      }
    }
    return () => { isMeasuring.set(false); };
  }, [list, renderItem, renderIds, eventsContainerWidth, assumeSameRowHeight, containerRef]);

  useElementDimensions('basic-inner-container', (width) => {
    if (width === eventsContainerWidth) return;
    savedMeasurementsWrapper.set({});
    setEventsContainerWidth(width);
  }, 200);

  const prevComputedRowHeight = usePreviousValue(computedRowHeight);
  const cachedContent = useCachedValue(() => list.map(
    (item, index) => (
      <div
        key={item}
        style={{
          height: computedRowHeight && (
            computedRowHeight instanceof Array ? computedRowHeight[index] : computedRowHeight
          ),
          overflowY: 'hidden',
        }}
        className="reverse-infinite-list-item"
      >
        {renderItem(item, index)}
      </div>
    ),
  ), computedRowHeight !== prevComputedRowHeight);
  const cachedIsNextPageLoading = useCachedValue(
    () => isNextPageLoading,
    isNextPageLoading || computedRowHeight !== prevComputedRowHeight,
  );

  const loadNextPageIfReady = useCallback(() => {
    if (isMeasuring.get()) return;
    loadNextPage();
  }, [loadNextPage]);

  const lastItem = _.last(list);
  const listRef = useRef<PatchedInfinite>();
  useLayoutEffect(() => { // Scroll to the bottom when a new item is appended
    if (!lastItem || typeof stickyAppendMargin !== 'number') return;
    const inst: any = listRef.current;
    if (!inst) return;
    const scrollDiff = inst.getLowestPossibleScrollTop();
    const scrollPosition = scrollDiff - inst.utils.getScrollTop();
    if (scrollPosition < stickyAppendMargin) inst.handleScroll(scrollDiff, 'layouteffect');
  }, [lastItem]);

  return (
    <Basic
      ref={listRef}
      containerRef={containerRef}
      rowHeight={computedRowHeight || -1}
      loadNextPage={loadNextPageIfReady}
      isNextPageLoading={cachedIsNextPageLoading}
      {...rest} // eslint-disable-line react/jsx-props-no-spreading
    >
      {cachedContent}
    </Basic>
  );
};
